import React, { ReactElement, useState } from "react";

import { Button, Input } from "@newsela/angelou";
import {
  withTrackEvent,
  actionNames,
  actionPrefixes,
  objectTypes,
} from "@newsela/snitch";
import { useRouter } from "next/router";

import {
  filterSearchBoxCss,
  searchBoxFilterWrapperCss,
} from "./SearchBoxFilter.style";

interface SearchBoxFilterProps {
  /**
   * Float label for the table search box filter input.
   */
  label: string;
  /**
   * Placeholder text for the table search box filter input.
   */
  placeholder: string;
  /**
   * onChange callback method that emit changes to the parent component.
   */
  onChange: (filters: Record<string, any>) => void;
  event?: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>;
  /**
   * onKeyDown callback method that emit changes to the parent component.
   */
  onKeyDown?: (filters: Record<string, any>) => void;
  e?: React.KeyboardEvent<HTMLInputElement>;
  /**
   * Admin table globalFilter values, represented as an object that includes
   * the search box filter and modal filter values applied to the admin table instance.
   */

  globalFilter: Record<string, any>;
  /**
   * Flag that enables the setting of the value of the search field by query param.
   */
  readQuerySearch: boolean;
}

const ButtonWithTrackEvent = withTrackEvent(Button);

const SearchBoxFilter = ({
  label,
  placeholder,
  globalFilter: globalFiltersValue,
  readQuerySearch,
  onChange,
}: SearchBoxFilterProps): ReactElement => {
  const { query } = useRouter() || {};
  const [filterInputValue, setFilterInputValue] = useState<
    string | number | null | string[]
  >(readQuerySearch && query && query["search"] ? query["search"] : "");
  const [showError, setShowError] = useState(false);

  const handleSearch = (
    filterObj: Record<string, string | number | null | string[]>,
  ) => {
    const queryString = filterObj["searchBoxFilterValue"]?.toString().trim();
    const searchInputHasMoreThanThreeChars = queryString
      ? queryString?.length >= 3
      : false;

    if (!searchInputHasMoreThanThreeChars) {
      setShowError(true);
    } else {
      setShowError(false);
      onChange({ ...filterObj, searchBoxFilterValue: queryString });
    }
  };

  return (
    <div css={searchBoxFilterWrapperCss}>
      <Input
        dataProps={{
          "data-qa-selector": `admin-console-directory-search-filter-text-input`,
        }}
        label={label}
        value={String(filterInputValue)}
        inputType={Input.inputTypes.text}
        placeholder={placeholder}
        onChange={({ target }) => {
          setFilterInputValue(target.value);
        }}
        onKeyDown={(e) => {
          if (e?.code === "Enter" && filterInputValue !== "") {
            handleSearch({
              ...globalFiltersValue,
              searchBoxFilterValue: filterInputValue,
            });
          }
        }}
        touched={showError}
        __cssFor={{ root: filterSearchBoxCss(showError) }}
        error={showError ? "At least 3 characters are required." : ""}
        underInputId="directory-table-search-field"
        isRequired={false}
      />
      <ButtonWithTrackEvent
        actionName={actionNames.search}
        actionPrefix={actionPrefixes.initiate}
        dataProps={{
          "data-qa-selector": `admin-console-directory-search-filter-button`,
        }}
        objectType={objectTypes.user}
        onClick={() =>
          handleSearch({
            ...globalFiltersValue,
            searchBoxFilterValue: filterInputValue,
          })
        }
        streamProperties={{
          description: `User searched ${filterInputValue}`,
        }}
      >
        Search
      </ButtonWithTrackEvent>
    </div>
  );
};

export default SearchBoxFilter;
